import type { BridgeMarketConfig } from '@cpc/tx';

function viteEnv(name: string): string | undefined {
	const v = import.meta.env[name];
	return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/** GetBlock shared RPC — override via VITE_BSC_RPC_URL / VITE_ETH_RPC_URL. */
export const DEFAULT_BSC_RPC_URL =
	'https://shared.us-east-1.getblock.io/9bfa92ca21e442838dc3dee7e1732393';
export const DEFAULT_ETH_RPC_URL =
	'https://shared.ap-southeast-1.getblock.io/37b164688f4e4681a7ee9733b2ed38b6';

/**
 * BSC mainnet Binance-Peg USDT (BEP-20) — official BNB Smart Chain USDT.
 * https://bscscan.com/token/0x55d398326f99059ff775485246999027b3197955
 */
export const BSC_USDT_MAINNET = '0x55d398326f99059fF775485246999027B3197955';
/** @deprecated use BSC_USDT_MAINNET */
export const BSC_BRIDGE_TOKEN = BSC_USDT_MAINNET;
/** Common BSC Chapel test USDT (override via VITE_BSC_USDT_ADDRESS). */
export const BSC_USDT_TESTNET = '0x337610d27c682E347C9cD60BD4b3b107C9d08463';

/** Bridge object ids from VITE_CPC_BRIDGE_* (.env.development / run_cpc_explorer.ps1). */
export function getBridgeMarketConfig(): BridgeMarketConfig | null {
	const packageId = viteEnv('VITE_CPC_BRIDGE_PACKAGE_ID');
	const bridgeId = viteEnv('VITE_CPC_BRIDGE_ID');
	if (!packageId || !bridgeId) return null;
	return { packageId, bridgeId };
}

export function getBscChainId(): number {
	const raw = viteEnv('VITE_BSC_CHAIN_ID');
	if (raw === '97') return 97;
	return 56;
}

export function getBscRpcUrl(): string | undefined {
	return viteEnv('VITE_BSC_RPC_URL');
}

export function getBscRpcUrlOrDefault(): string {
	return getBscRpcUrl() ?? DEFAULT_BSC_RPC_URL;
}

export function getEthRpcUrl(): string | undefined {
	return viteEnv('VITE_ETH_RPC_URL');
}

export function getEthRpcUrlOrDefault(): string {
	return getEthRpcUrl() ?? DEFAULT_ETH_RPC_URL;
}

/** Legacy wCPC bridge; USDT flow uses escrow instead. */
export function getBscBridgeContract(): string | undefined {
	return viteEnv('VITE_BSC_BRIDGE_CONTRACT');
}

/** BSC USDT escrow (production mainnet — fixed). */
export const BSC_USDT_ESCROW_MAINNET = '0xf5CE271AF596dBff0A21a9768d5109d0a01caA9b';

/** BSC USDT escrow contract; env override optional. */
export function getBscUsdtEscrowContract(): string | undefined {
	return viteEnv('VITE_BSC_USDT_ESCROW') ?? BSC_USDT_ESCROW_MAINNET;
}

export function getBscUsdtTokenAddress(useTestnet: boolean): string {
	const override = viteEnv('VITE_BSC_USDT_ADDRESS');
	if (override) return override;
	return useTestnet ? BSC_USDT_TESTNET : BSC_USDT_MAINNET;
}

/** USDT decimals on BSC (Binance-Peg USDT uses 18). */
export function getBscUsdtDecimals(): number {
	const raw = viteEnv('VITE_BSC_USDT_DECIMALS');
	if (raw) {
		const n = Number.parseInt(raw, 10);
		if (Number.isFinite(n) && n >= 0) return n;
	}
	return 18;
}

/** Demo / relayer rate: USDT paid per 1 DATA (default "1" = 1:1). */
export function getBridgeCpcUsdtRate(): number {
	const raw = viteEnv('VITE_BRIDGE_CPC_USDT_RATE') ?? '1';
	const n = Number.parseFloat(raw);
	return Number.isFinite(n) && n > 0 ? n : 1;
}

/**
 * Optional prefill for BSC recipient (DGC → USDT) when MetaMask not connected.
 * Production default is empty — never show a hard-coded third-party address.
 * Override only for local demos: VITE_BRIDGE_DEFAULT_BSC_RECIPIENT=0x...
 */
export function getDefaultBscRecipientAddress(): string {
	return (viteEnv('VITE_BRIDGE_DEFAULT_BSC_RECIPIENT') ?? '').trim();
}

/**
 * Optional prefill for DGC recipient (USDT → DGC) when CPC wallet not connected.
 * Production default is empty.
 */
export function getDefaultCpcRecipientAddress(): string {
	return (viteEnv('VITE_BRIDGE_DEFAULT_CPC_RECIPIENT') ?? '').trim();
}

export function cpcAmountToUsdt(amountCpc: string, rate = getBridgeCpcUsdtRate()): string {
	const cpc = Number.parseFloat(amountCpc);
	if (!Number.isFinite(cpc) || cpc <= 0) return '';
	const usdt = cpc * rate;
	return trimDecimals(usdt, 2);
}

export function usdtAmountToCpc(amountUsdt: string, rate = getBridgeCpcUsdtRate()): string {
	const usdt = Number.parseFloat(amountUsdt);
	if (!Number.isFinite(usdt) || usdt <= 0) return '';
	const cpc = usdt / rate;
	return trimDecimals(cpc, 2);
}

/** 跨链桥数量：最多两位小数，允许输入过程中的空串和末尾小数点。 */
export function sanitizeBridgeAmountInput(raw: string): string {
	let s = raw.replace(/[^\d.]/g, '');
	const firstDot = s.indexOf('.');
	if (firstDot !== -1) {
		s = `${s.slice(0, firstDot + 1)}${s.slice(firstDot + 1).replace(/\./g, '')}`;
		const [whole, frac = ''] = s.split('.');
		s = `${whole}.${frac.slice(0, 2)}`;
	}
	if (/^0\d/.test(s)) {
		s = s.replace(/^0+/, '') || '0';
	}
	return s;
}

export function isValidBridgeAmount(raw: string): boolean {
	const s = raw.trim();
	if (!s || s.endsWith('.')) return false;
	if (!/^\d+(\.\d{1,2})?$/.test(s)) return false;
	const n = Number.parseFloat(s);
	return Number.isFinite(n) && n >= 1;
}

function trimDecimals(n: number, maxFrac: number): string {
	const s = n.toFixed(maxFrac);
	return s.replace(/\.?0+$/, '') || '0';
}

export { getCpcRpcUrl, getPushHubWsUrl } from './clobConfig';
